import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { LogOut, Phone, Mail, X, Search, Calendar } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { CATEGORIES } from "@/lib/data";
import Logo from "@/components/shared/Logo";

const STATUSES = ["new", "contacted", "closed"];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [inquiries, setInquiries] = useState([]);
  const [stats, setStats] = useState({ new: 0, contacted: 0, closed: 0, total: 0 });
  const [filter, setFilter] = useState({
    status: "", city: "", category: "", q: "", date_from: "", date_to: "",
  });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.status) params.status = filter.status;
      if (filter.city) params.city = filter.city;
      if (filter.category) params.category = filter.category;
      if (filter.q) params.q = filter.q;
      if (filter.date_from) params.date_from = filter.date_from;
      if (filter.date_to) params.date_to = filter.date_to;
      const [{ data: list }, { data: s }] = await Promise.all([
        api.get("/inquiries", { params }),
        api.get("/stats"),
      ]);
      setInquiries(list);
      setStats(s);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    // Debounce so text-filter typing (q, city) doesn't hammer the API on every keystroke.
    const t = setTimeout(() => { fetchAll(); }, 300);
    return () => clearTimeout(t);
  }, [fetchAll]);

  const updateStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/inquiries/${id}`, { status });
      setInquiries((arr) => arr.map((i) => (i.id === id ? data : i)));
      setSelected((s) => (s && s.id === id ? data : s));
      const { data: s2 } = await api.get("/stats");
      setStats(s2);
      toast.success(`Marked as ${status}.`);
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F7FA] text-[#0B2545]" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="border-b border-line bg-white">
        <div className="mx-auto max-w-[1800px] px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Logo className="text-[20px]" tone="navy" />
            <span className="overline">Command center</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden md:inline text-xs text-[#5B6472] font-mono">{user?.email}</span>
            <button onClick={logout} className="btn-ghost-light !py-2 !px-3 text-xs" data-testid="admin-logout">
              <LogOut className="h-3.5 w-3.5" strokeWidth={1.8} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1800px] px-6 md:px-10 py-10 md:py-14">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#E5E7EB] border border-line mb-10">
          <StatTile label="Total inquiries" value={stats.total} tone="navy" />
          <StatTile label="New" value={stats.new} tone="accent" testid="stat-new" />
          <StatTile label="Contacted" value={stats.contacted} tone="navy" testid="stat-contacted" />
          <StatTile label="Closed" value={stats.closed} tone="muted" testid="stat-closed" />
        </div>

        {/* Filters — row 1: search + status + city + category + reset */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-[#E5E7EB] border border-line mb-[1px]">
          <FilterInput
            label="Search"
            icon={<Search className="h-3.5 w-3.5" strokeWidth={1.8} />}
            value={filter.q} onChange={(v) => setFilter((f) => ({ ...f, q: v }))}
            testid="filter-search" placeholder="Name, email, phone"
          />
          <FilterSelect
            label="Status" value={filter.status} onChange={(v) => setFilter((f) => ({ ...f, status: v }))}
            options={[{ v: "", l: "All statuses" }, ...STATUSES.map((s) => ({ v: s, l: s }))]}
            testid="filter-status"
          />
          <FilterInput
            label="City / State"
            value={filter.city} onChange={(v) => setFilter((f) => ({ ...f, city: v }))}
            testid="filter-city" placeholder="e.g. Phoenix or Arizona"
          />
          <FilterSelect
            label="Category" value={filter.category} onChange={(v) => setFilter((f) => ({ ...f, category: v }))}
            options={[{ v: "", l: "All categories" }, ...CATEGORIES.map((c) => ({ v: c.name, l: c.name }))]}
            testid="filter-category"
          />
          <button
            onClick={() => setFilter({ status: "", city: "", category: "", q: "", date_from: "", date_to: "" })}
            className="bg-white text-[#0B2545] hover:bg-[#F5F7FA] transition-colors text-xs font-mono uppercase tracking-widest px-5 py-4 text-left"
            data-testid="filter-reset"
          >
            Reset filters
          </button>
        </div>

        {/* Filters — row 2: pickup date range */}
        <div className="grid grid-cols-2 gap-px bg-[#E5E7EB] border border-line mb-8">
          <FilterInput
            label="Pickup from"
            icon={<Calendar className="h-3.5 w-3.5" strokeWidth={1.8} />}
            type="date"
            value={filter.date_from} onChange={(v) => setFilter((f) => ({ ...f, date_from: v }))}
            testid="filter-date-from"
          />
          <FilterInput
            label="Pickup to"
            icon={<Calendar className="h-3.5 w-3.5" strokeWidth={1.8} />}
            type="date"
            value={filter.date_to} onChange={(v) => setFilter((f) => ({ ...f, date_to: v }))}
            testid="filter-date-to"
          />
        </div>

        {/* Table */}
        <div className="border border-line bg-white overflow-hidden">
          <div className="border-b border-line px-5 py-3 flex items-center justify-between">
            <span className="overline">{inquiries.length} inquiries</span>
            <span className="overline">{loading ? "Loading…" : "Live"}</span>
          </div>

          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line text-left bg-[#F5F7FA]">
                  <Th>Received</Th>
                  <Th>Name</Th>
                  <Th>Contact</Th>
                  <Th>City</Th>
                  <Th>Category</Th>
                  <Th>Dates</Th>
                  <Th>Status</Th>
                </tr>
              </thead>
              <tbody>
                {inquiries.map((i) => (
                  <tr
                    key={i.id}
                    className="border-b border-line hover:bg-[#F5F7FA] cursor-pointer transition-colors"
                    onClick={() => setSelected(i)}
                    data-testid={`inquiry-row-${i.id}`}
                  >
                    <Td className="text-[#5B6472] font-mono text-xs whitespace-nowrap">
                      {new Date(i.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </Td>
                    <Td className="text-[#0B2545] font-medium">{i.full_name}</Td>
                    <Td className="text-[#5B6472]">
                      <div className="flex flex-col text-xs">
                        <span>{i.phone}</span>
                        <span className="text-[#5B6472]/80">{i.email}</span>
                      </div>
                    </Td>
                    <Td className="text-[#0B2545]">{i.pickup_city}</Td>
                    <Td className="text-[#0B2545]">{i.car_category || <span className="text-[#94A0AE] italic">— no preference</span>}</Td>
                    <Td className="text-[#5B6472] text-xs whitespace-nowrap">
                      {i.pickup_date} → {i.dropoff_date}
                    </Td>
                    <Td><StatusPill status={i.status} /></Td>
                  </tr>
                ))}
                {!loading && inquiries.length === 0 && (
                  <tr><td colSpan={7} className="p-16 text-center text-[#5B6472]">No inquiries found for these filters.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile */}
          <ul className="md:hidden divide-y divide-[#E5E7EB]">
            {inquiries.map((i) => (
              <li key={i.id} className="p-5 active:bg-[#F5F7FA]" onClick={() => setSelected(i)} data-testid={`inquiry-mobile-${i.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="font-serif text-2xl text-[#0B2545] leading-tight">{i.full_name}</div>
                    <div className="text-xs text-[#5B6472] mt-1 font-mono">{i.pickup_city} · {i.car_category || "no preference"}</div>
                    <div className="text-xs text-[#5B6472]/80 mt-1">{i.pickup_date} → {i.dropoff_date}</div>
                  </div>
                  <StatusPill status={i.status} />
                </div>
              </li>
            ))}
            {!loading && inquiries.length === 0 && (
              <li className="p-10 text-center text-[#5B6472]">No inquiries found.</li>
            )}
          </ul>
        </div>
      </div>

      <AnimatePresence>
        {selected && (
          <InquiryDrawer inquiry={selected} onClose={() => setSelected(null)} onUpdate={updateStatus} />
        )}
      </AnimatePresence>
    </div>
  );
}

function StatTile({ label, value, tone = "navy", testid }) {
  const color = tone === "accent" ? "text-[#0B2545]" : tone === "muted" ? "text-[#5B6472]" : "text-[#0B2545]";
  const accent = tone === "accent" ? "bg-[#0B2545] text-white" : "bg-white";
  return (
    <div className={`${accent} p-6 md:p-8`} data-testid={testid}>
      <div className={`overline mb-3 ${tone === "accent" ? "text-white/70" : ""}`}>{label}</div>
      <div className={`font-serif text-5xl md:text-6xl leading-none tracking-tight ${tone === "accent" ? "text-white" : color}`}>{value}</div>
    </div>
  );
}

function FilterInput({ label, icon, value, onChange, testid, placeholder, type = "text" }) {
  return (
    <label className="bg-white block">
      <div className="overline px-5 pt-3 flex items-center gap-2">{icon} {label}</div>
      <input
        type={type} value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-transparent outline-none text-[#0B2545] text-sm px-5 py-2.5"
        data-testid={testid}
      />
    </label>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <label className="bg-white block">
      <div className="overline px-5 pt-3">{label}</div>
      <select
        value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent outline-none text-[#0B2545] text-sm px-5 py-2.5"
        data-testid={testid}
      >
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </label>
  );
}

function Th({ children }) {
  return <th className="px-5 py-3 font-mono text-[10px] tracking-[0.20em] uppercase text-[#5B6472] font-medium">{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-5 py-4 ${className}`}>{children}</td>;
}

function StatusPill({ status }) {
  const cfg = {
    new: "bg-[#0B2545] text-white border-[#0B2545]",
    contacted: "bg-white text-[#0B2545] border-[#0B2545]",
    closed: "bg-[#F5F7FA] text-[#5B6472] border-line",
  }[status] || "bg-white text-[#0B2545] border-line";
  return (
    <span className={`inline-block border ${cfg} font-mono text-[10px] tracking-widest uppercase px-2.5 py-1`}>
      {status}
    </span>
  );
}

function InquiryDrawer({ inquiry, onClose, onUpdate }) {
  return (
    <>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 z-40 bg-[#0B2545]/40 backdrop-blur-sm"
      />
      <motion.aside
        initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="fixed top-0 right-0 bottom-0 z-50 w-full md:w-[560px] bg-white border-l border-line overflow-y-auto"
        data-testid="inquiry-drawer"
      >
        <div className="p-6 flex items-center justify-between border-b border-line">
          <span className="overline">Inquiry · {inquiry.id.slice(0, 8)}</span>
          <button onClick={onClose} className="border border-line h-9 w-9 inline-flex items-center justify-center hover:border-[#0B2545]" data-testid="drawer-close">
            <X className="h-4 w-4" strokeWidth={1.8} />
          </button>
        </div>

        <div className="p-8">
          <div className="overline mb-3">{new Date(inquiry.created_at).toLocaleString()}</div>
          <h2 className="font-serif text-4xl md:text-5xl leading-tight text-[#0B2545]">
            {inquiry.full_name}
          </h2>

          <div className="mt-8 grid grid-cols-2 gap-px bg-[#E5E7EB] border border-line">
            <a href={`tel:${inquiry.phone}`} className="bg-white p-5 hover:bg-[#F5F7FA]" data-testid="drawer-phone">
              <div className="overline mb-2">Phone</div>
              <div className="flex items-center gap-3 text-[#0B2545]">
                <Phone className="h-4 w-4" strokeWidth={1.8} />
                <span className="font-mono text-sm">{inquiry.phone}</span>
              </div>
            </a>
            <a href={`mailto:${inquiry.email}`} className="bg-white p-5 hover:bg-[#F5F7FA]" data-testid="drawer-email">
              <div className="overline mb-2">Email</div>
              <div className="flex items-center gap-3 text-[#0B2545]">
                <Mail className="h-4 w-4" strokeWidth={1.8} />
                <span className="text-sm break-all">{inquiry.email}</span>
              </div>
            </a>
          </div>

          <dl className="mt-10 space-y-3">
            <Row label="Pickup city" value={inquiry.pickup_city} />
            <Row label="Category" value={inquiry.car_category || "— No preference"} />
            <Row label="Preferred provider" value={inquiry.preferred_provider || "— No preference"} />
            <Row label="Pickup date" value={inquiry.pickup_date} />
            <Row label="Drop-off date" value={inquiry.dropoff_date} />
            <Row label="Lead driver age" value={inquiry.lead_driver_age} />
          </dl>

          {inquiry.message && (
            <div className="mt-10 border-t border-line pt-6">
              <div className="overline mb-3">Notes</div>
              <p className="text-[#5B6472] leading-relaxed whitespace-pre-wrap">{inquiry.message}</p>
            </div>
          )}

          <div className="mt-12 border-t border-line pt-6">
            <div className="overline mb-4">Status</div>
            <div className="grid grid-cols-3 gap-px bg-[#E5E7EB] border border-line">
              {STATUSES.map((s) => (
                <button
                  key={s} onClick={() => onUpdate(inquiry.id, s)}
                  className={`px-4 py-4 font-mono text-[10px] tracking-widest uppercase transition-colors ${
                    inquiry.status === s
                      ? "bg-[#0B2545] text-white"
                      : "bg-white text-[#0B2545] hover:bg-[#F5F7FA]"
                  }`}
                  data-testid={`status-${s}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>
      </motion.aside>
    </>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-6 border-b border-line pb-3">
      <span className="overline">{label}</span>
      <span className="text-[#0B2545] text-sm text-right font-medium">{value}</span>
    </div>
  );
}
